/** Shared encoder/decoder for the People/Company search page's ``?q=`` param.
 *
 *  The ``/search`` route hydrates from ``q = encodeSearchQuery({ tab,
 *  filters })`` on mount, applies the filters, and auto-runs the search
 *  (see the hydration effect in ``search-page-container.component.tsx``).
 *  Any caller that wants to deep-link into a pre-filled search must produce
 *  the identical shape, so the encode/decode logic lives here to keep the
 *  search page and its callers from drifting apart. */

import { INITIAL_FILTERS } from '@/types/search-filters';
import { INITIAL_COMPANY_FILTERS } from '@/utils/company-search.utils';
import { COMPOSITE_KEY } from '@/utils/legacy-search-migration';

/** app.cleanlist.ai answers 414 above about 10.2 KB of URL (probed
 *  2026-10-16), so a search longer than this gets no link. */
export const MAX_SEARCH_LINK_LENGTH = 8000;

export type SearchQueryPayload = {
  tab: 'people' | 'company';
  filters: Record<string, unknown>;
};

// UTF-8 safe, URL-safe base64. Plain ``btoa`` throws InvalidCharacterError on
// any code point > 0xFF, so a company with a non-Latin name or LinkedIn slug
// (Cyrillic, CJK, Greek, accents) would otherwise crash the encoder
// mid-navigation and strand the caller. Encode to UTF-8 bytes first; chunk the
// String.fromCharCode call so a large selection can't blow the call stack.
//
// URL-safe alphabet (RFC 4648 §5: ``-``/``_``, no ``=`` padding) so a shared
// link needs no percent-encoding. Standard base64's ``+`` turns into a space as
// soon as anything decodes the link once more (mail link-wrappers, chat apps,
// a URL pasted together by hand), and the search silently didn't load.
function toBase64Url(str: string): string {
  const bytes = new TextEncoder().encode(str);
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Accepts both alphabets, so links shared before the URL-safe switch still
// open. A space can only be a ``+`` some hop decoded: put it back (``atob``
// would otherwise drop it and decode garbage). ``atob`` takes unpadded input.
function fromBase64Url(b64: string): string {
  const binary = atob(b64.replace(/[- ]/g, '+').replace(/_/g, '/'));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    // A link from before UTF-8 encoding: plain ``btoa`` of Latin-1 text
    // ("São Paulo"), whose bytes are the characters themselves.
    return binary;
  }
}

export function encodeSearchQuery(payload: SearchQueryPayload): string {
  return toBase64Url(JSON.stringify(payload));
}

// A filter at its default adds nothing to the Finder's own link: the page
// fills every missing key back in when it opens one (migrateSearchFilters /
// migrateCompanyFilters).
function withoutDefaults(
  filters: Record<string, unknown>,
  defaults: object
): Record<string, unknown> {
  const initial = defaults as Record<string, unknown>;
  return Object.fromEntries(
    Object.entries(filters).filter(
      ([key, value]) =>
        !(
          key in initial &&
          JSON.stringify(value) === JSON.stringify(initial[key])
        )
    )
  );
}

/** The search without the filters left at their default. The Finder's
 *  address bar and the searches it stores on the server both use it, so a
 *  link stays short and the server's de-duplication hash stays stable. */
export function slimSearch({
  tab,
  filters,
}: SearchQueryPayload): SearchQueryPayload {
  const slim = withoutDefaults(
    filters,
    tab === 'company' ? INITIAL_COMPANY_FILTERS : INITIAL_FILTERS
  );
  // People carry their company firmographics in a slice of their own.
  const composite = slim[COMPOSITE_KEY];
  if (composite && typeof composite === 'object') {
    const set = withoutDefaults(
      composite as Record<string, unknown>,
      INITIAL_COMPANY_FILTERS
    );
    if (Object.keys(set).length) slim[COMPOSITE_KEY] = set;
    else delete slim[COMPOSITE_KEY];
  }
  return { tab, filters: slim };
}

/** The Finder's ``?q=`` for its address bar. Other producers (Clu, MCP) keep
 *  the full shape their readers check. */
export function encodeSearchLink(payload: SearchQueryPayload): string {
  return encodeSearchQuery(slimSearch(payload));
}

/** Decode a ``?q=`` value. Hardened: ``q`` is a user-craftable URL param, so
 *  never throw on malformed input (bad base64 / bad JSON / non-object). Returns
 *  ``null`` on anything unparseable so the search page falls back to its
 *  default state instead of crashing. Backward-compatible with legacy
 *  standard-base64 links, including plain ASCII-only ``btoa`` encodes. */
export function decodeSearchQuery(
  q: string
): { tab?: string; filters?: Record<string, unknown> } | null {
  try {
    const parsed: unknown = JSON.parse(fromBase64Url(q));
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed as { tab?: string; filters?: Record<string, unknown> };
  } catch {
    return null;
  }
}
